import {
  ArrowLeftOutlined,
  DisconnectOutlined,
  MoreOutlined,
  PoweroffOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  WifiOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { Dropdown, Tag, Modal, Tooltip} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { logout } from "@seims/shared/src/security";
import {
  confirmAction,
  OfflineDataViewer,
  useHeader,
  useOfflineQueueControl,
  usePWA,
} from "@seims/shared";

/**
 * EventsHeader
 *
 * Fully self-contained layout header.
 * AppLayout simply renders <EventsHeader />.
 */
export const EventsHeader = () => {
  const navigate = useNavigate();
  const [isOfflineDataViewerOpen, setIsOfflineDataViewerOpen] = useState(false);
  const { isOnline } = usePWA();
  const { headerState, resetHeader } = useHeader();
  const { queueCount } = useOfflineQueueControl();

  const handleBack = () => {
    if (headerState.onBack) {
      headerState.onBack();
    } else{      
      navigate("/events");
    }
    resetHeader();
  };

  const openOfflineDataDownload = () => {
    setIsOfflineDataViewerOpen(true);
  };

  // Action Handlers
  const handleProfile = () => {
    navigate("/profile");
  };

  const handleNeedHelp = () => {
    navigate("/events/help");
  };

  const handleLogout = () => {
    // Delegates to Keycloak logout
    logout();
  };

  const showDiscardConfirm = () => {
    confirmAction({
      title: "Discard changes?",
      description: "Are you sure you want to go back? All unsaved changes will be lost.",
      cancelText: "No",
      okText: "Yes, discard",
      onOk: () => navigate("/"),
    });
  };

  // Menu Config
  const menuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: (
        <Tooltip
          title={isOnline ? "" : "Available only when online"}
          trigger={["hover", "click"]}
        >
          <span>Profile</span>
        </Tooltip>
      ),
      onClick: handleProfile,
      disabled: !isOnline,
    },
    // {
    //   key: "sync-offline",
    //   icon: isSyncing ? <SyncOutlined spin /> : <SyncOutlined />,
    //   label: `Sync Offline Data${queueCount > 0 ? ` (${queueCount})` : ""}`,
    //   onClick: handleSyncOfflineData,
    //   disabled: !isOnline || queueCount === 0 || isSyncing,
    // },
    {
      key: "sync-offline",
      icon: <DownloadOutlined />,
      label: (
        <Tooltip
          title={isOnline ? "" : "Connect to the internet to sync data"}
          trigger={["hover", "click"]}
        >
          <span>
            Download/Sync Data{queueCount > 0 ? ` (${queueCount})` : ""}
          </span>
        </Tooltip>
      ),
      onClick: openOfflineDataDownload,
      disabled: !isOnline,
    },
    {
      key: "events-help",
      icon: <QuestionCircleOutlined />,
      label: "Need help?",
      onClick: handleNeedHelp,
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <PoweroffOutlined />,
      label: isOnline ? (
        "Log Out"
      ) : (
        <Tooltip 
          title="Logout requires network connection"
          trigger={["hover", "click"]}
        >
          <span>Log Out</span>
        </Tooltip>
      ),
      onClick: handleLogout,
      disabled: !isOnline,
    },
  ];

  const headerClassName = headerState?.headerClassName === undefined ? `events-header` : `events-header ${headerState?.headerClassName}`;
  
  return (
    <>
    <div className={headerClassName}>
      {/* LEFT: Back arrow + dynamic title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color:'#fff'  }}>
        {(headerState.showBack && headerState.showConfirmBack) && (
          <ArrowLeftOutlined style={{ fontSize: '18px', cursor: 'pointer', color:'#fff' }} onClick={showDiscardConfirm} />
        )}
        {(headerState.showBack && !headerState.showConfirmBack) && (
          <ArrowLeftOutlined
            onClick={handleBack}
            style={{ fontSize: '18px', cursor: 'pointer', color:'#fff' }}
          />) 
        }
        <h2 className="events-header__title">{headerState.title}</h2>
      </div>

        <div className="events-header__right">

          <Tag
            icon={
              isOnline ? (
                <WifiOutlined />
              ) : (
                <DisconnectOutlined />
              )
            }
            className={
              isOnline
                ? "events-header__badge events-header__badge--online"
                : "events-header__badge events-header__badge--offline"
            }
          >
            {isOnline ? "Online" : "Offline"}
          </Tag>

          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: menuItems,
              className: "events-header-menu",
            }}
          >
            <MoreOutlined className="events-header__menu-trigger" />
          </Dropdown>
        </div>
      </div>
      <OfflineDataViewer
        triggerButton={false}
        open={isOfflineDataViewerOpen}
        onOpenChange={setIsOfflineDataViewerOpen}
      />
    </>
  );
};
